import { useSyncExternalStore } from "react";

/**
 * A minimal, dependency-free global store (same pattern as
 * usePaywallDialog.ts) so anything -- the periodic version poll in
 * VersionWatcher, or a failed lazy-chunk import somewhere else in the
 * app -- can show the same calm "a new version is available" banner
 * without needing to force a reload itself.
 *
 * This exists specifically to fix a real, confirmed bug: a failed
 * dynamic import (a stale tab's chunk hash 404ing after a new deploy)
 * previously triggered an unconditional hard reload via
 * forceAppUpdate() -- window.location.replace() -- which wipes any
 * in-progress, uncommitted state, including whatever someone was
 * mid-typing into a search box. Showing this banner instead, and
 * letting the person choose when to actually reload, fixes that
 * without losing the "something needs a refresh" signal.
 */
let isOpen = false;
const listeners = new Set<() => void>();

function notify() {
  Array.from(listeners).forEach((l) => l());
}

export function showUpdateBanner() {
  isOpen = true;
  notify();
}

export function useUpdateBannerOpen() {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => isOpen
  );
}

/**
 * True if the user is genuinely mid-typing into a real, focused text
 * input, textarea, or contenteditable element right now. Used to
 * decide whether a chunk-load failure should show a calm banner
 * (typing) or is safe to auto-reload immediately (not typing, e.g. a
 * failure during a route navigation with no text input in play).
 */
export function isTypingInInput(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA") return true;
  if (el instanceof HTMLElement && el.isContentEditable) return true;
  return false;
}
